import type { Entity } from './components'
import { tierRadiusMult } from './evolution'
import { hypertrophyRadiusMult } from './powers'
import { T } from './tunables'

// Recomputes player.renderable.radius from base × hypertrophy × eating × tier.
// No-op if baseRadius is unset (non-player entities, test stubs without base).
// NOTE: skillActiveSystem temporarily mutates sizeFromEating by ±ENGULF_RADIUS_BONUS
// while engulf is active. If you change this formula, verify engulf still activates/deactivates cleanly.
// NOTE: while a tier morph is in flight, evolutionSystem calls this every
// frame so the radius lerps smoothly toward the new tier's value (and through
// the dip/overshoot of the three-phase curve — tierRadiusMult is morph-aware).
export function applyPlayerRadius(player: Entity): void {
  if (!player.renderable || player.baseRadius == null) return
  const eatingMult = 1 + (player.sizeFromEating ?? 0)
  player.renderable.radius =
    player.baseRadius * hypertrophyRadiusMult(player) * eatingMult * tierRadiusMult(player)
}

// Called when a microbe is fully absorbed. Increments eating growth and recomputes radius.
// `units` scales the increment for bigger meals (reversal-eaten predators).
export function growPlayerFromEating(player: Entity, units = 1): void {
  if (player.baseRadius == null) return
  const current = player.sizeFromEating ?? 0
  player.sizeFromEating = Math.min(current + T.growth.sizeDeltaPerMicrobe * units, T.growth.maxSizeFromEating)
  applyPlayerRadius(player)
}

// Reversal rule (VS study): anything whose body is under playerRadius/eatRatio
// is food, predator or not. Collision starts the absorb; predator-ai flees.
export function playerCanEat(player: Entity, e: Entity): boolean {
  const pr = player.renderable?.radius ?? T.player.baseRadius
  const er = e.renderable?.radius ?? Infinity
  return pr >= er * T.predator.eatRatio
}

// Shrink primitive (shared by Sporulation now, Lysosome Bloom later): attacks
// that soften rather than kill — pushing bodies toward the reversal threshold
// instead of deleting them. Floor keeps creatures render-sane; DNA payout
// shrinks with the body, which is the honest cost of pre-softening a meal.
export function shrinkCreature(e: Entity, amount: number): void {
  if (!e.renderable) return
  e.renderable.radius = Math.max(T.growth.shrinkFloor, e.renderable.radius - amount)
}
